import {
    PipelineDefinition,
    PipelineStepInstance,
    PipelineTransition,
    TransitionEnterBehavior,
    TransitionExitGate,
} from './pipelineBuilder.types'
import { getStepConfig } from './stepConfigUtils'
import { labelForInviteTemplate } from './inviteEmailTemplateOptions'
import { getCatalogEntry } from './stepCatalog'
import {
    conversionRulesFromTransitionConfig,
    summarizeConversionRules,
} from './conversionRulesUtils'
import { summarizeAllModeExit } from './exitFromLeadsUtils'
import {
    getTransitionConfig,
    isLeadsExitFromStep,
    isProspectsToLeadsTransition,
} from './transitionConfigUtils'
import {
    defaultExitModeForFromType,
    labelForTrigger,
    PM_MANUAL_TRIGGER,
} from './transitionTriggerCatalog'

export function transitionId(fromStepId: string, toStepId: string): string {
    return `${fromStepId}__${toStepId}`
}

export function deriveEnterBehavior(
    step: PipelineStepInstance,
): TransitionEnterBehavior {
    if (step.type === 'leads') {
        const config = getStepConfig(step, 'leads')
        if (config.enter_behavior) {
            return config.enter_behavior
        }
    }

    if (step.enter_behavior) {
        return step.enter_behavior
    }
    if (step.entry.mode === 'manual') {
        return 'pm_start'
    }
    return 'auto_start'
}

export function deriveTransition(
    fromStep: PipelineStepInstance,
    toStep: PipelineStepInstance,
    transitionConfigs?: PipelineDefinition['transition_configs'],
): PipelineTransition {
    const config = getTransitionConfig(fromStep, toStep, transitionConfigs)
    const exitMode =
        config.exit_mode ?? defaultExitModeForFromType(fromStep.type)
    const isConversionEdge = isProspectsToLeadsTransition(fromStep, toStep)
    const conversionRules = isConversionEdge
        ? conversionRulesFromTransitionConfig(config)
        : undefined

    return {
        id: transitionId(fromStep.id, toStep.id),
        fromStepId: fromStep.id,
        toStepId: toStep.id,
        exitMode,
        exitGate: config.require_pm_approval ? 'pm_review' : 'none',
        enterBehavior: deriveEnterBehavior(toStep),
        reviewPrompt: config.review_prompt,
        triggers: config.triggers,
        conversionRules,
        allowPmOverride: config.allow_pm_override,
    }
}

export function buildLinearTransitions(
    enabledSteps: PipelineStepInstance[],
    transitionConfigs?: PipelineDefinition['transition_configs'],
): PipelineTransition[] {
    const transitions: PipelineTransition[] = []
    for (let i = 0; i < enabledSteps.length - 1; i += 1) {
        transitions.push(
            deriveTransition(
                enabledSteps[i],
                enabledSteps[i + 1],
                transitionConfigs,
            ),
        )
    }
    return transitions
}

function summarizeProspectsStep(step: PipelineStepInstance): string {
    const config = getStepConfig(step, 'prospects')
    const parts: string[] = []
    if (config.auto_enroll_email_campaign) {
        parts.push('Auto-enroll email campaign')
    }
    if (config.auto_enroll_phone_campaign) {
        parts.push('Auto-enroll phone campaign')
    }
    return parts.length > 0
        ? parts.join(' · ')
        : 'Configure campaign enrollment'
}

function summarizeLeadsStep(step: PipelineStepInstance): string {
    const config = getStepConfig(step, 'leads')
    const parts: string[] = []

    const enter = deriveEnterBehavior(step)
    if (enter === 'queue' || enter === 'pm_start') {
        parts.push('Place in list on arrival')
    } else {
        parts.push('Auto-start on arrival')
    }

    if (config.on_enter_automations.send_account_invite?.enabled !== false) {
        const templateId =
            config.on_enter_automations.send_account_invite?.template_id
        parts.push(`Account invite (${labelForInviteTemplate(templateId)})`)
    }

    const handbookScope =
        config.application_handbook?.scope ?? 'workbook_complete'
    if (handbookScope === 'identity_complete') {
        parts.push('Identity handbook only')
    } else {
        parts.push('Entire application handbook')
    }

    return parts.join(' · ')
}

export function summarizeNode(step: PipelineStepInstance): string {
    if (step.type === 'prospects') {
        return summarizeProspectsStep(step)
    }
    if (step.type === 'leads') {
        return summarizeLeadsStep(step)
    }

    const enter = deriveEnterBehavior(step)
    const instanceLabel = step.config?.instance_label
    const hasInstanceLabel =
        typeof instanceLabel === 'string' && instanceLabel.trim().length > 0

    const behaviorPart =
        enter === 'pm_start'
            ? 'PM starts each person'
            : enter === 'queue'
            ? 'Queue on arrival'
            : 'Auto-start on arrival'

    if (hasInstanceLabel) {
        return behaviorPart
    }

    const catalog = getCatalogEntry(step.type)
    return `${catalog.label} · ${behaviorPart}`
}

const ENTER_BEHAVIOR_LABELS: Record<TransitionEnterBehavior, string> = {
    queue: 'Place in next list only',
    auto_start: 'Auto-start next step',
    pm_start: 'PM starts each lead',
}

const EXIT_GATE_SHORT: Record<TransitionExitGate, string> = {
    none: 'Auto advance',
    pm_review: 'PM approves',
}

const ENTER_BEHAVIOR_SHORT: Record<TransitionEnterBehavior, string> = {
    queue: 'Queue only',
    auto_start: 'Auto-start',
    pm_start: 'PM starts',
}

function formatTriggers(
    fromStep: PipelineStepInstance,
    triggers: string[],
    maxLabels = 2,
    joiner: 'and' | 'or' = 'or',
): string {
    const conditionTriggers = triggers.filter(
        (trigger) => trigger !== PM_MANUAL_TRIGGER,
    )
    if (conditionTriggers.length === 0) {
        return 'No exit conditions'
    }
    const labels = conditionTriggers.map((trigger) =>
        labelForTrigger(fromStep.type, trigger),
    )
    if (labels.length <= maxLabels) {
        return joiner === 'and'
            ? labels.length === 1
                ? labels[0]
                : labels.slice(0, -1).join(', ') +
                  ' and ' +
                  labels[labels.length - 1]
            : labels.join(', ')
    }
    const remaining = labels.length - maxLabels
    const prefix = labels.slice(0, maxLabels).join(', ')
    return `${prefix} +${remaining} more`
}

export function summarizeTransitionShort(
    transition: PipelineTransition,
    fromStep?: PipelineStepInstance,
    toStep?: PipelineStepInstance,
): string {
    if (
        fromStep &&
        toStep &&
        isProspectsToLeadsTransition(fromStep, toStep) &&
        transition.conversionRules
    ) {
        return summarizeConversionRules(transition.conversionRules)
    }

    if (fromStep && transition.exitMode === 'all') {
        return summarizeAllModeExit(
            transition.triggers,
            transition.allowPmOverride ?? false,
            fromStep.type,
        )
    }

    if (!fromStep) {
        return `${EXIT_GATE_SHORT[transition.exitGate]} → ${
            ENTER_BEHAVIOR_SHORT[transition.enterBehavior]
        }`
    }

    const joiner = transition.exitMode === 'all' ? 'and' : 'or'
    const triggers = formatTriggers(fromStep, transition.triggers, 1, joiner)
    const approval =
        transition.exitGate === 'pm_review' ? 'PM approves' : 'Move when'
    const toLabel = toStep ? getCatalogEntry(toStep.type).label : 'next step'
    return `${approval}: ${triggers} → ${toLabel}`
}

export function summarizeTransition(
    fromStep: PipelineStepInstance,
    toStep: PipelineStepInstance,
    transition?: PipelineTransition,
): string {
    const t = transition ?? deriveTransition(fromStep, toStep)

    if (isProspectsToLeadsTransition(fromStep, toStep) && t.conversionRules) {
        return summarizeConversionRules(t.conversionRules)
    }

    if (t.exitMode === 'all') {
        const toLabel = getCatalogEntry(toStep.type).label
        const conditions = summarizeAllModeExit(
            t.triggers,
            t.allowPmOverride ?? false,
            fromStep.type,
        )
        const enter = ENTER_BEHAVIOR_LABELS[t.enterBehavior]
        return `Leave Leads → ${toLabel}: ${conditions}. On arrival: ${enter}.`
    }

    const fromLabel = getCatalogEntry(fromStep.type).label
    const toLabel = getCatalogEntry(toStep.type).label
    const triggers = formatTriggers(fromStep, t.triggers)
    const approval =
        t.exitGate === 'pm_review'
            ? 'PM must approve before moving'
            : 'Move automatically when'
    const enter = ENTER_BEHAVIOR_LABELS[t.enterBehavior]
    return `${fromLabel} → ${toLabel}: ${approval} ${triggers}. On arrival: ${enter}.`
}

export function applyTransitionToSteps(
    fromStep: PipelineStepInstance,
    toStep: PipelineStepInstance,
    transition: PipelineTransition,
): { fromStep: PipelineStepInstance; toStep: PipelineStepInstance } {
    const conditionTriggers = transition.triggers.filter(
        (trigger) => trigger !== PM_MANUAL_TRIGGER,
    )
    const primaryTrigger =
        conditionTriggers[0] ??
        transition.triggers[0] ??
        fromStep.completion.condition

    const requiresAnyApproval =
        transition.conversionRules?.some(
            (rule) =>
                rule.enabled &&
                rule.require_pm_approval &&
                rule.trigger !== PM_MANUAL_TRIGGER,
        ) ?? transition.exitGate === 'pm_review'

    let onCompleteAction = fromStep.on_complete.action
    if (requiresAnyApproval) {
        onCompleteAction = 'require_review'
    } else if (fromStep.type === 'prospects') {
        onCompleteAction = 'hold'
    } else if (isLeadsExitFromStep(fromStep) && transition.exitMode === 'all') {
        onCompleteAction = 'auto_advance'
    } else if (fromStep.type === 'leads') {
        onCompleteAction = 'manual'
    } else {
        onCompleteAction = 'auto_advance'
    }

    const nextFrom: PipelineStepInstance = {
        ...fromStep,
        completion: {
            condition: primaryTrigger,
        },
        on_complete: {
            ...fromStep.on_complete,
            action: onCompleteAction,
            review_prompt: requiresAnyApproval
                ? transition.reviewPrompt
                : undefined,
        },
    }

    return {
        fromStep: nextFrom,
        toStep,
    }
}

export function syncEnterBehaviorOnStep(
    step: PipelineStepInstance,
    enterBehavior: TransitionEnterBehavior,
): PipelineStepInstance {
    const nextStep: PipelineStepInstance = {
        ...step,
        enter_behavior: enterBehavior,
        entry: {
            ...step.entry,
            mode: enterBehavior === 'pm_start' ? 'manual' : 'auto',
        },
    }

    if (step.type === 'leads') {
        const config = getStepConfig(step, 'leads')
        return {
            ...nextStep,
            config: {
                ...config,
                enter_behavior: enterBehavior,
            },
        }
    }

    return nextStep
}
